import { useState } from "react";
import {
    Container,
    Stack,
    Title,
    Card,
    Group,
    Text,
    Button,
    Select,
    Badge,
    Loader,
} from "@mantine/core";
import { IconUsers, IconClock, IconTrophy } from "@tabler/icons-react";
import {
    useJoinCombineQueueMutation,
    useLeaveCombineQueueMutation,
    useMyCombineQueueStatusQuery,
} from "generated/graphql";
import { notifications } from "@mantine/notifications";

/**
 * CombineQueue
 *
 * The main queue page for Combine matchmaking. Players can:
 * - Select their tier
 * - Join the matchmaking queue
 * - View their queue status (position, estimated wait time)
 * - Leave the queue
 * - See active matches
 * - Get notified when a lobby is created (queue pop)
 */
export default function CombineQueue() {
    const [hasJoinError, setHasJoinError] = useState(false);

    // GraphQL hooks - stop polling if there was a join error
    const { data: queueData, loading: queueLoading, refetch } = useMyCombineQueueStatusQuery({
        pollInterval: hasJoinError ? 0 : 3000, // Stop polling if join failed
    });

    const [joinQueue, { loading: joiningQueue }] = useJoinCombineQueueMutation({
        onCompleted: (data) => {
            if (data.joinCombineQueue.success) {
                setHasJoinError(false); // Clear error state on success
                notifications.show({
                    title: "Joined Queue",
                    message: data.joinCombineQueue.message || "You have joined the combine queue",
                    color: "green",
                });
                refetch();
            } else {
                setHasJoinError(true); // Set error state to stop polling
                notifications.show({
                    title: "Unable to Join Queue",
                    message: data.joinCombineQueue.message || "Could not join the queue",
                    color: "red",
                });
            }
        },
        onError: (error) => {
            setHasJoinError(true); // Set error state to stop polling
            notifications.show({
                title: "Error",
                message: error.message,
                color: "red",
            });
        },
    });

    const [leaveQueue, { loading: leavingQueue }] = useLeaveCombineQueueMutation({
        onCompleted: () => {
            setHasJoinError(false); // Clear error state when leaving queue
            notifications.show({
                title: "Left Queue",
                message: "You have left the combine queue",
                color: "blue",
            });
            refetch();
        },
        onError: (error) => {
            notifications.show({
                title: "Error",
                message: error.message,
                color: "red",
            });
        },
    });

    const isQueued = queueData?.myCombineQueueStatus?.isQueued || false;
    const queuePosition = queueData?.myCombineQueueStatus?.position;
    const estimatedWaitTime = queueData?.myCombineQueueStatus?.estimatedWaitTime;
    const queuedTierName = queueData?.myCombineQueueStatus?.tier?.tierName;

    const handleJoinQueue = async () => {
        setHasJoinError(false); // Reset error state before attempting to join
        await joinQueue();
    };

    const handleLeaveQueue = async () => {
        await leaveQueue();
    };

    return (
        <Container fluid p="md">
            <Stack gap="md">
                {/* Page Header */}
                <Group justify="space-between" align="center">
                    <Title order={2}>Combine Matchmaking</Title>
                    <Badge size="lg" variant="light" color="blue" leftSection={<IconUsers size={16} />}>
                        Competitive 5v5
                    </Badge>
                </Group>

                {/* Join Queue Card */}
                {!isQueued && (
                    <Card withBorder radius="md" p="lg">
                        <Stack gap="md">
                            <div>
                                <Text size="lg" fw={600} mb="xs">
                                    Combine Queue
                                </Text>
                                <Text size="sm" c="dimmed">
                                    Join the combine matchmaking queue. You'll be automatically placed in the appropriate skill tier
                                    and matched with players of similar ability for competitive 5v5 matches.
                                </Text>
                            </div>

                            <Button
                                size="lg"
                                leftSection={<IconTrophy size={20} />}
                                loading={joiningQueue}
                                onClick={handleJoinQueue}
                                fullWidth
                            >
                                Join Queue
                            </Button>
                        </Stack>
                    </Card>
                )}

                {/* Queue Status Card (shown when queued) */}
                {isQueued && (
                    <Card withBorder radius="md" p="lg" bg="blue.0">
                        <Stack gap="md">
                            <Group justify="space-between" align="center">
                                <div>
                                    <Text size="lg" fw={600} c="blue">
                                        In Queue
                                    </Text>
                                    <Text size="sm" c="dimmed">
                                        Searching for players...
                                    </Text>
                                </div>
                                <Loader color="blue" size="md" />
                            </Group>

                            <Group grow>
                                <div>
                                    <Text size="xs" c="dimmed" mb={4}>Position</Text>
                                    <Text size="xl" fw={700}>
                                        {queuePosition !== undefined && queuePosition !== null
                                            ? `#${queuePosition}`
                                            : "-"}
                                    </Text>
                                </div>
                                <div>
                                    <Text size="xs" c="dimmed" mb={4}>Estimated Wait</Text>
                                    <Group gap="xs">
                                        <IconClock size={20} />
                                        <Text size="xl" fw={700}>
                                            {estimatedWaitTime
                                                ? `${Math.ceil(estimatedWaitTime / 60)}m`
                                                : "-"}
                                        </Text>
                                    </Group>
                                </div>
                                <div>
                                    <Text size="xs" c="dimmed" mb={4}>Tier</Text>
                                    <Badge size="lg" variant="filled">
                                        {queuedTierName || "-"}
                                    </Badge>
                                </div>
                            </Group>

                            <Button
                                variant="outline"
                                color="red"
                                fullWidth
                                loading={leavingQueue}
                                onClick={handleLeaveQueue}
                            >
                                Leave Queue
                            </Button>
                        </Stack>
                    </Card>
                )}

                {/* Active Matches Section */}
                <Card withBorder radius="md" p="lg">
                    <Stack gap="md">
                        <div>
                            <Text size="lg" fw={600}>
                                Active Matches
                            </Text>
                            <Text size="sm" c="dimmed">
                                Watch live combine matches in progress
                            </Text>
                        </div>

                        <Text size="sm" c="dimmed" ta="center" py="xl">
                            No active matches at the moment
                        </Text>
                    </Stack>
                </Card>
            </Stack>
        </Container>
    );
}
